import React, { useCallback, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Typography } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { Toolbar } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { ToolbarButton } from '@mui/x-data-grid-pro';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Add } from '@mui/icons-material';
import { Remove } from '@mui/icons-material';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { CategoricalValueRows } from 'libs/end-to-end/src/data-generator/data-generator-input';

const CustomToolbar: React.FC<{
  rows: CategoricalValueRows;
  setRows: SetterWithReducer<CategoricalValueRows>;
  rowSelectionModel: GridRowSelectionModel | undefined;
}> = ({ rows, setRows, rowSelectionModel }) => {
  const handleNewValue = useCallback(() => {
    const nextId =
      rows.length === 0
        ? 0
        : Math.max(...rows.map((r) => Number(r.id) || 0)) + 1;
    setRows((prev) => [...prev, { id: nextId, value: 'New Value' }]);
  }, [rows, setRows]);

  const handleRemoveValue = useCallback(() => {
    if (!rowSelectionModel) {
      return;
    }

    setRows((prev) => {
      if (rowSelectionModel.type === 'include') {
        return prev.filter((row) => !rowSelectionModel.ids.has(row.id));
      }
      return prev.filter((row) => rowSelectionModel.ids.has(row.id));
    });
  }, [rowSelectionModel, setRows]);

  const hasSelection = useMemo(() => {
    if (!rowSelectionModel) {
      return false;
    }

    if (rowSelectionModel.type === 'include') {
      return rowSelectionModel.ids.size > 0;
    }

    return rows.length > rowSelectionModel.ids.size;
  }, [rowSelectionModel, rows.length]);

  return (
    <Toolbar>
      <Stack
        direction={'row'}
        spacing={1}
        sx={{ flex: 1, mx: 0.5, alignItems: 'center' }}
      >
        <Typography fontWeight="medium">Values</Typography>
        <Tooltip title="Add new value">
          <ToolbarButton aria-label="new-value" onClick={handleNewValue}>
            <Add fontSize="small" />
          </ToolbarButton>
        </Tooltip>

        <Tooltip title="Remove selected columns">
          <ToolbarButton disabled={!hasSelection} onClick={handleRemoveValue}>
            <Remove fontSize="small" />
          </ToolbarButton>
        </Tooltip>
      </Stack>
    </Toolbar>
  );
};

const dataGridColDefs: GridColDef<CategoricalValueRows[number]>[] = [
  { field: 'value', headerName: 'Value', width: 300, editable: true },
];

export const NewColumnCategoricalValuesDataGrid: React.FC<{
  rows: CategoricalValueRows;
  setRows: SetterWithReducer<CategoricalValueRows>;
  isLoading: boolean;
}> = ({ rows, setRows, isLoading }) => {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });

  const processRowUpdate = useCallback(
    (
      newRow: CategoricalValueRows[number],
      oldRow: CategoricalValueRows[number],
    ) => {
      const updatedRow = { ...oldRow, ...newRow };

      setRows((prev) =>
        prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)),
      );

      return updatedRow;
    },
    [setRows],
  );

  return (
    <DataGridPro
      sx={{ maxHeight: 450 }}
      columns={dataGridColDefs}
      rows={rows}
      loading={isLoading}
      slots={{
        toolbar: () => (
          <CustomToolbar
            rows={rows}
            setRows={setRows}
            rowSelectionModel={selectionModel}
          />
        ),
      }}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={(newModel) => {
        setSelectionModel(newModel);
      }}
      isRowSelectable={(params) => true}
      isRowReorderable={(params) => true}
      processRowUpdate={processRowUpdate}
      disableColumnMenu
      showToolbar
      rowReordering
      checkboxSelection
      disableColumnSorting={true}
    />
  );
};
